// @spec RSK-STR-002
'use client';

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { riskListSearchSchema, RiskRegisterWorkspace } from '@/features/risks';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/risks/')({
  validateSearch: riskListSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.riskRegister'),
  }),
  component: RisksListPage,
});

/**
 * Risks list page - main entry point for risk management.
 */
function RisksListPage() {
  const { t } = useTranslation('risks');
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/risks/' });
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('register.noOrg')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('register.noOrgDescription')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('register.goToOrgs')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl">
        <RiskRegisterWorkspace
          search={search}
          onSearchChange={(nextSearch) => {
            void navigate({
              search: nextSearch as never,
              replace: true,
            });
          }}
          orgId={activeOrg.id}
          organizationName={activeOrg.name}
          showModuleContext
        />
      </div>
    </div>
  );
}
